import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast/headless'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { SESDnsRecord, SESDomainStatus } from '../../types'
import { useResolver } from '../../hooks'
import Alert from '../../ui/Alert'
import Button from '../../ui/Button'
import { CheckCircleIcon, CopyIcon } from '../../ui/icons'
import FormWrapper from '../../ui/form/FormWrapper'
import TextInput from '../../ui/form/TextInput'
import Modal, { ModalProps } from '../../ui/Modal'
import './DomainSetup.css'

const StatusIcon = ({ status }: { status?: string }) => {
    if (status === 'SUCCESS') {
        return <div className="domain-record-status verified"><CheckCircleIcon /></div>
    }
    if (status === 'FAILED') {
        return <div className="domain-record-status failed">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="icon">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
        </div>
    }
    return <div className="domain-record-status pending">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="icon">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
    </div>
}

const stripDomain = (name: string, domain: string) => {
    const suffix = `.${domain}`
    return name.endsWith(suffix) ? name.slice(0, -suffix.length) : name
}

const RecordRow = ({ record, domain, short }: { record: SESDnsRecord, domain: string, short: boolean }) => {
    const { t } = useTranslation()
    const displayName = short ? stripDomain(record.name, domain) : record.name

    const handleCopy = async (value: string) => {
        await navigator.clipboard.writeText(value)
        toast.success(t('copied'))
    }

    return (
        <div className="domain-record">
            <span className="domain-record-type">{record.type}</span>
            <div className="domain-record-field">
                <span className="record-value" title={displayName}>{displayName}</span>
                <Button
                    icon={<CopyIcon />}
                    variant="plain"
                    size="tiny"
                    onClick={async () => await handleCopy(displayName)}
                />
            </div>
            <div className="domain-record-field">
                <span className="record-value" title={record.value}>{record.value}</span>
                <Button
                    icon={<CopyIcon />}
                    variant="plain"
                    size="tiny"
                    onClick={async () => await handleCopy(record.value)}
                />
            </div>
            {record.required
                ? <StatusIcon status={record.status} />
                : <div className="domain-record-status" />
            }
        </div>
    )
}

const RecordSection = ({ title, badge, records, domain, short }: { title: string, badge?: string, records: SESDnsRecord[], domain: string, short: boolean }) => {
    const { t } = useTranslation()
    if (!records.length) return null
    return (
        <div className="domain-records-section">
            <h4>
                {title}
                {badge && <span className="domain-badge recommended">{badge}</span>}
            </h4>
            <div className="domain-record-labels">
                <span>{t('domain_record_type')}</span>
                <span>{t('domain_record_name')}</span>
                <span>{t('domain_record_value')}</span>
                <span />
            </div>
            {records.map((record, i) => (
                <RecordRow key={i} record={record} domain={domain} short={short} />
            ))}
        </div>
    )
}

interface DomainFormValues {
    domain: string
}

interface DomainSetupModalProps extends Omit<ModalProps, 'title'> {
    providerId: number
}

const RECHECK_INTERVAL = 60

export default function DomainSetupModal({ providerId, ...props }: DomainSetupModalProps) {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [isChecking, setIsChecking] = useState(false)
    const [shortHostnames, setShortHostnames] = useState(true)
    const [countdown, setCountdown] = useState(RECHECK_INTERVAL)
    const countdownRef = useRef<ReturnType<typeof setInterval>>()
    const checkRef = useRef<() => Promise<void>>()

    const [domainStatus, setDomainStatus] = useResolver<SESDomainStatus | null>(
        useCallback(async () => {
            if (!providerId) return null
            return await api.providers.getDomain(project.id, 'email', 'ses', providerId)
        }, [project.id, providerId]),
    )

    const handleCreateDomain = async ({ domain }: DomainFormValues) => {
        if (!providerId) return
        const result = await api.providers.createDomain(project.id, 'email', 'ses', providerId, domain)
        setDomainStatus(result)
    }

    const handleCheckStatus = async () => {
        if (!providerId) return
        setIsChecking(true)
        try {
            const result = await api.providers.checkDomain(project.id, 'email', 'ses', providerId)
            setDomainStatus(result)
            setCountdown(RECHECK_INTERVAL)
            if (result.verifiedForSending && result.dkimStatus === 'SUCCESS') {
                toast.success(t('domain_all_verified'))
            }
        } finally {
            setIsChecking(false)
        }
    }

    checkRef.current = handleCheckStatus

    const isVerified = domainStatus?.configured
        && domainStatus.verifiedForSending
        && domainStatus.dkimStatus === 'SUCCESS'

    useEffect(() => {
        if (!props.open || !domainStatus?.configured || isVerified) {
            clearInterval(countdownRef.current)
            return
        }

        setCountdown(RECHECK_INTERVAL)
        countdownRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    void checkRef.current?.()
                    return RECHECK_INTERVAL
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(countdownRef.current)
    }, [props.open, domainStatus?.configured, isVerified])

    const dkimRecords = domainStatus?.records?.filter(r => r.purpose === 'dkim') ?? []
    const mailFromRecords = domainStatus?.records?.filter(r => r.purpose === 'mail_from_mx' || r.purpose === 'mail_from_spf') ?? []
    const dmarcRecords = domainStatus?.records?.filter(r => r.purpose === 'dmarc') ?? []

    return (
        <Modal
            {...props}
            title={t('domain_setup')}
            size="large"
        >
            <div className="domain-setup">
                {!domainStatus?.configured && (
                    <>
                        <Alert title={t('domain_setup')} variant="plain">
                            {t('domain_setup_intro')}
                        </Alert>
                        <FormWrapper<DomainFormValues>
                            onSubmit={handleCreateDomain}
                            submitLabel={t('domain_setup_button')}
                            defaultValues={{ domain: '' }}
                        >
                            {form => (
                                <TextInput.Field
                                    form={form}
                                    name="domain"
                                    label={t('domain_input_label')}
                                    placeholder="example.com"
                                    subtitle={t('domain_input_subtitle')}
                                    required
                                />
                            )}
                        </FormWrapper>
                    </>
                )}

                {domainStatus?.configured && (
                    <>
                        {isVerified
                            ? <Alert title={t('domain_all_verified')} variant="success">
                                {t('domain_verified_desc')}
                            </Alert>
                            : <Alert title={t('domain_dns_instructions_title')} variant="info">
                                {t('domain_dns_instructions')}
                            </Alert>
                        }

                        <label className="domain-hostname-toggle">
                            <div className="switch">
                                <input
                                    type="checkbox"
                                    checked={shortHostnames}
                                    onChange={e => setShortHostnames(e.target.checked)}
                                />
                                <div className="slider round"></div>
                            </div>
                            <span>{t('domain_short_hostnames')}</span>
                        </label>

                        <RecordSection title={t('domain_section_dkim')} records={dkimRecords} domain={domainStatus.name!} short={shortHostnames} />
                        <RecordSection title={t('domain_section_mailfrom')} records={mailFromRecords} domain={domainStatus.name!} short={shortHostnames} />
                        <RecordSection title={t('domain_section_dmarc')} badge={t('domain_recommended')} records={dmarcRecords} domain={domainStatus.name!} short={shortHostnames} />

                        <p className="domain-note">{t('domain_dns_note')}</p>

                        {!isVerified && (
                            <div className="domain-actions">
                                <Button
                                    onClick={handleCheckStatus}
                                    isLoading={isChecking}
                                >
                                    {t('domain_check_status')}
                                </Button>
                                <span className="domain-countdown">
                                    {t('domain_rechecking_in', { seconds: countdown })}
                                </span>
                            </div>
                        )}
                    </>
                )}
            </div>
        </Modal>
    )
}
